import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import api from "../../services/api";

const ENDPOINT_BY_TYPE = {
  company: (id) => `/tech_links/companies/${id}`,
  equipment: (id) => `/tech_links/equipments/${id}`
};

const TITLE_BY_TYPE = {
  company: "Técnico responsável pelo cliente",
  equipment: "Técnico responsável pela máquina"
};

const hintStyle = { marginTop: 8, color: "#6c757d" };

/**
 * Vincula o técnico responsável a um cliente ou a uma máquina.
 * Chamados abertos para o cliente/máquina são direcionados a esse técnico
 * (o da máquina tem prioridade); se ele estiver ausente, vão para o reserva dele.
 */
const TechLinkDialog = ({ visible, onHide, type, id, title }) => {
  const queryClient = useQueryClient();
  const toast = useRef(null);
  const [techId, setTechId] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data: techs = [], isLoading: techsLoading } = useQuery(
    ["tech-link-techs"],
    () => api.get("/tech_links/techs").then((r) => r.data),
    { staleTime: 5 * 60 * 1000, enabled: visible }
  );

  const { data: link, isLoading: linkLoading } = useQuery(
    ["tech-link", type, id],
    () => api.get(ENDPOINT_BY_TYPE[type](id)).then((r) => r.data),
    { enabled: visible && !!id && !!type }
  );

  useEffect(() => {
    setTechId(link?.tech_id ?? null);
  }, [link, visible]);

  const loading = techsLoading || linkLoading;
  const options = techs.map((t) => ({ label: t.name, value: t.id }));

  // Técnico vinculado que foi inativado depois não vem na lista de disponíveis:
  // mantém visível para o ADM perceber e trocar
  if (link?.tech && !techs.some((t) => t.id === link.tech.id)) {
    options.unshift({ label: `${link.tech.name} (inativo)`, value: link.tech.id });
  }
  const selectedTech = techs.find((t) => t.id === techId);

  const handleSave = async () => {
    if (saving || !type || !id) return;
    setSaving(true);
    try {
      await api.put(ENDPOINT_BY_TYPE[type](id), { tech_id: techId });
      toast.current?.show({
        severity: "success",
        summary: "",
        detail: "Técnico responsável atualizado.",
        life: 4000
      });
      queryClient.invalidateQueries(["tech-link"]);
      onHide();
    } catch (err) {
      const errors = err?.response?.data?.errors;
      toast.current?.show({
        severity: err?.response?.status === 422 ? "warn" : "error",
        summary: "",
        detail: errors
          ? Object.values(errors).flat()[0]
          : "Não foi possível atualizar o técnico responsável.",
        life: 5000
      });
      if (err?.response?.status === 422) {
        queryClient.invalidateQueries(["tech-link-techs"]);
      }
    } finally {
      setSaving(false);
    }
  };

  const companyTech = link?.company_link?.tech;

  const footer = (
    <div>
      <Button
        label="Cancelar"
        className="p-button-text"
        onClick={onHide}
        disabled={saving}
      />
      <Button
        label="Salvar"
        icon="pi pi-check"
        onClick={handleSave}
        loading={saving}
        disabled={loading}
      />
    </div>
  );

  return (
    <>
      <Toast ref={toast} position="bottom-right" />
      <Dialog
        header={title || TITLE_BY_TYPE[type] || "Técnico responsável"}
        visible={visible}
        style={{ width: "450px" }}
        modal
        footer={footer}
        onHide={onHide}
        closable={!saving}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label htmlFor="tech-link-dropdown" style={{ fontWeight: 600 }}>
            Técnico responsável
          </label>
          <Dropdown
            id="tech-link-dropdown"
            value={techId}
            options={options}
            onChange={(e) => setTechId(e.value ?? null)}
            placeholder={loading ? "Carregando..." : "Sem técnico vinculado"}
            filter
            showClear
            disabled={loading}
          />

          <label
            htmlFor="tech-link-backup"
            style={{ fontWeight: 600, marginTop: 8 }}
          >
            Técnico reserva
          </label>
          <InputText
            id="tech-link-backup"
            value={
              !selectedTech
                ? ""
                : selectedTech.backup_tech?.name ?? "Sem reserva definido"
            }
            placeholder="Definido pelo técnico escolhido"
            disabled
          />
          <small style={{ color: "#6c757d" }}>
            O reserva é definido no cadastro do técnico, em "Técnicos".
          </small>

          {type === "equipment" && !techId && (
            <small style={hintStyle}>
              {companyTech
                ? `Sem vínculo próprio: os chamados desta máquina vão para o técnico do cliente (${
                    companyTech.name
                  }${
                    companyTech.backup_tech
                      ? `, reserva ${companyTech.backup_tech.name}`
                      : ""
                  }).`
                : "Sem vínculo próprio e o cliente também não tem técnico: o ADM escolhe na aprovação."}
            </small>
          )}

          {type === "equipment" && techId && (
            <small style={hintStyle}>
              O técnico da máquina tem prioridade sobre o técnico do cliente.
            </small>
          )}
        </div>
      </Dialog>
    </>
  );
};

export default TechLinkDialog;
